import { Component, EventEmitter, OnInit, Output,ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BrandRequest, BrandTableResponse, BrandTableResponsePaginatedList, BrandsService } from '@src/app/shared/backend/clients';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { FilterOperator } from '@src/app/shared/enums/FilterOperator.enum';
import { SessionService } from '@src/app/shared/services/session.service';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { ConfirmationService } from 'primeng/api';

interface BrandDropdownOption {
  id: number;
  text: string;
}

@Component({
    selector: 'app-brands-settings',
    templateUrl: './brands-settings.component.html',
    styleUrls: ['./brands-settings.component.scss'],
    host: {
        class: 'app-brands-settings'
    },
    providers: [ConfirmationService],
    standalone: false
})
export class BrandsSettingsComponent implements OnInit {

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;
  @Output() createBrandRequested = new EventEmitter<void>();

  uploadedLogoUrl: string | null = null;
  brandLogoLoadFailed = false;
  file: File | null = null;
  fileUploadError = '';
  updateLogo = false;

  paginatorFirst: number = 0;
  paginatorRows: number = 10;
  totalRecords: number = 0;

  defaultBrandOptions: BrandDropdownOption[] = [];
  defaultBrandId: number | null | undefined = null;
  showDefaultBrandUpdateModal = false;
  updatingDefaultBrand = false;
  brandToSetDefault: BrandDropdownOption | null = null;

  paginatedBrands: BrandTableResponse[] = [];
  page: BrandTableResponsePaginatedList = {};
  filterOperator = FilterOperator;

  loading = false;
  savingBrand = false;

  showBrandModalbox = false;
  selectedBrandId: number = 0;

  selectedDigitalCardBrandId: number | null = null;
  displayDigitalCardSettingsModalbox = false;

  showDeleteBrandModalbox = false;
  selectedBrandIdToDelete: number = 0;
  brandNameToDelete: string = '';
  deletingBrand = false;

  public brandForm: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(50)]],
  });

  public defaultBrandForm: FormGroup = this.fb.group({
    defaultBrandId: [[]],
  })

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private brandsService: BrandsService,
    private sessionService: SessionService,
    private confirmationService: ConfirmationService
  ) { }

  async ngOnInit() {
    this.reset();
    await this.getBrands();
    await this.getDefaultBrandOptions();
    await this.setDefaultBrand(true);
  }

  private async getBrands(): Promise<void> {
    this.loading = true;

    const payload = {
      pageNumber: this.getPageNumber(),
      pageSize: this.paginatorRows
    };

    try {
      this.page = await firstValueFrom(this.brandsService.apiBrandsFilterTablePost(payload));
      this.paginatedBrands = this.page.results ?? [];
      this.totalRecords = this.page.totalCount ?? this.paginatedBrands.length;
    } catch (error) {
      console.error('Error fetching brands:', error);

      this.page = {};
      this.paginatedBrands = [];
      this.totalRecords = 0;
    } finally {
      this.loading = false;
    }
  }

  private async getDefaultBrandOptions(): Promise<void> {
    if (this.totalRecords === 0) {
      this.defaultBrandOptions = [];
      return;
    }

    const payload = {
      pageNumber: 1,
      pageSize: this.totalRecords
    };

    try {
      const allBrandsPage = await firstValueFrom(
        this.brandsService.apiBrandsFilterTablePost(payload)
      );

      this.defaultBrandOptions = (allBrandsPage.results ?? [])
        .filter(brand => brand.id != null && brand.isActive)
        .map(brand => ({
          id: brand.id!,
          text: brand.name ?? ''
        }));
    } catch (error) {
      console.error('Error fetching default brand options:', error);
      this.defaultBrandOptions = [];
    }
  }

  onPageChange(event: any) {
    this.paginatorFirst = event.first;
    this.paginatorRows = event.rows;

    this.getBrands();
  }

  reset(): void {
    this.brandForm.reset({
      name: ''
    });

    this.uploadedLogoUrl = null;
    this.brandLogoLoadFailed = false;
    this.file = null;
    this.fileUploadError = '';
    this.updateLogo = false;
  }

  openCreateBrandModalbox() {
    this.selectedBrandId = 0;
    this.reset();
    this.showBrandModalbox = true;
  }

  async onViewBrand(event: MouseEvent, brand: BrandTableResponse) {
    event.preventDefault();

    if (!brand.id) {
      return;
    }

    try {
      const brandResponse = await firstValueFrom(this.brandsService.apiBrandsIdGet(brand.id));
      const brandLogoResponse = await firstValueFrom(this.brandsService.apiBrandsIdLogoGet(brand.id));

      this.reset();

      this.selectedBrandId = brand.id;
      this.brandForm.patchValue({
        name: brandResponse.name,
      });

      const existingLogoBase64 = brandLogoResponse.response;
      this.uploadedLogoUrl = existingLogoBase64 ? `data:image/png;base64,${existingLogoBase64}` : null;

      this.showBrandModalbox = true;
    } catch (error) {
      console.error('Error fetching brand:', error);
    }
  }

  closeBrandModalbox() {
    this.showBrandModalbox = false;
    this.selectedBrandId = 0;
    this.reset();
  }

  onLogoLoadError() {
    this.brandLogoLoadFailed = true;
  }

  onBrandLogoRemove() {
    this.uploadedLogoUrl = null;
    this.file = null;
    this.brandLogoLoadFailed = false;
    this.fileUploadError = '';
    this.updateLogo = true;
  }

  showUploadPrompt() {
    this.uploadedLogoUrl = null;
    this.file = null;
    this.brandLogoLoadFailed = false;
    this.fileUploadError = '';
    this.updateLogo = false;
  }

  onBrandLogoSelected(event: any) {
    this.onBrandLogoUpload(event);
  }

  onBrandLogoUpload(event: any) {
    const uploadedFile: File | undefined = event?.files?.[0];

    if (!uploadedFile) {
      return;
    }

    const maxSizeBytes = 10 * 1024 * 1024;

    if (uploadedFile.type !== 'image/png') {
      this.file = null;
      this.fileUploadError = this.translate.instant('SYSTEM_SETTINGS.BRANDS.LOGO_FILE_VALIDATION');
      return;
    }

    if (uploadedFile.size > maxSizeBytes) {
      this.file = null;
      this.fileUploadError = this.translate.instant('SYSTEM_SETTINGS.BRANDS.LOGO_FILE_SIZE_VALIDATION');
      return;
    }

    this.file = uploadedFile;
    this.fileUploadError = '';
    this.updateLogo = true;
    this.brandLogoLoadFailed = false;

    const fileReader = new FileReader();

    fileReader.onload = () => {
      this.uploadedLogoUrl = fileReader.result as string;
    };

    fileReader.readAsDataURL(uploadedFile);
  }

  private async getLogoBytes(): Promise<string | null> {
    if (!this.file) {
      return null;
    }

    const fileBuffer = await this.file.arrayBuffer();
    const logoBytes = new Uint8Array(fileBuffer);

    return JSON.stringify(Array.from(logoBytes));
  }

  async saveBrand() {
    if (this.brandForm.invalid) {
      this.brandForm.markAllAsTouched();
      return;
    }

    this.savingBrand = true;

    try {
      if (this.selectedBrandId) {
        await this.updateBrand();
      } else {
        await this.createBrand();
        this.paginatorFirst = 0;
      }

      this.showBrandModalbox = false;
      this.selectedBrandId = 0;
      this.reset();

      await this.getBrands();
      await this.getDefaultBrandOptions();
      await this.setDefaultBrand(false);
    } catch (error) {
      console.error('Error saving brand:', error);
    } finally {
      this.savingBrand = false;
    }
  }

  private async createBrand(): Promise<void> {
    const payload: BrandRequest = {
      name: this.brandForm.controls['name'].value,
      logo: await this.getLogoBytes(),
      updateLogo: this.updateLogo
    };

    await firstValueFrom(this.brandsService.apiBrandsCreatePost(payload));
  }

  private async updateBrand(): Promise<void> {
    const payload: BrandRequest = {
      name: this.brandForm.controls['name'].value,
      logo: await this.getLogoBytes(),
      updateLogo: this.updateLogo
    };

    await firstValueFrom(this.brandsService.apiBrandsUpdateIdPut(this.selectedBrandId, payload));
  }

  async onStateChange(event: any, brand: BrandTableResponse) {
    const stateNewValue = event.checked;
    const oldValue = !stateNewValue;
    if (!brand.id) {
      return;
    }
    try {
      if (!stateNewValue) {
        await firstValueFrom(this.brandsService.apiBrandsIdDeactivatePut(brand.id));
      }
      else if (stateNewValue) {
        await firstValueFrom(this.brandsService.apiBrandsIdActivatePut(brand.id));
      }
    } catch(error) {
      console.error('Error changing brand state:', error);
      event.preventDefault();
      event.checked = oldValue;
    }
    
    // State update confirmation dialog
    this.confirmationService.confirm({
      key: 'confirm-state-change-dialog',
      header: this.translate.instant('MODALBOXES.TITLE_CONFIRM_STATE_CHANGE')
    });
    await this.getBrands();
    await this.getDefaultBrandOptions();
    await this.setDefaultBrand(false);
  }

  private getPageNumber(): number {
    return Math.floor(this.paginatorFirst / this.paginatorRows) + 1;
  }

  displayDeleteBrandModalbox(event: MouseEvent, brand: BrandTableResponse) {
    event.preventDefault();

    this.selectedBrandIdToDelete = brand.id ?? 0;
    this.brandNameToDelete = brand.name ?? '';
    this.showDeleteBrandModalbox = true;
  }

  cancelDeleteBrand() {
    this.showDeleteBrandModalbox = false;
    this.selectedBrandIdToDelete = 0;
    this.brandNameToDelete = '';
  }

  async showDeleteDialog(event: MouseEvent, brand: BrandTableResponse): Promise<void> {
    const brandId = Number(brand.id ?? 0);
    const deleteCheckResponses = await firstValueFrom(this.brandsService.
      apiBrandsDeleteCheckBatchPost([brandId]));

    this.dialog?.show(deleteCheckResponses);
    this.selectedBrandIdToDelete = brandId;
  }

  async deleteBrand(doDelete: boolean) : Promise<void>{
    if (!this.selectedBrandIdToDelete) {
      return;
    }

     if (doDelete) {
      try{  
      await firstValueFrom(this.brandsService.apiBrandsDeleteIdDelete(this.selectedBrandIdToDelete));
      await this.getBrands();
      await this.getDefaultBrandOptions();
      await this.setDefaultBrand(false);
    } catch (error) {
      console.error('Error deleting brand:', error);
    } finally {
      this.deletingBrand = false;
    }
   }
  }

  openDigitalCardSettingsModalbox(brand: BrandTableResponse): void {
    if (brand.id == null) {
      return;
    }

    this.selectedDigitalCardBrandId = brand.id;
    this.displayDigitalCardSettingsModalbox = true;
  }

  closeDigitalCardSettingsModalbox(): void {
    this.displayDigitalCardSettingsModalbox = false;
    this.selectedDigitalCardBrandId = null;
  }

  private getDefaultBrandDropdownValue(): number[] {
    if (this.defaultBrandId == null) {
      return [];
    }

    return [Number(this.defaultBrandId)];
  }

  private async setDefaultBrand(reloadCompanyData: boolean){
    if (reloadCompanyData) {
      const companyResponse = await this.sessionService.getCompanyData(true);
      this.defaultBrandId = companyResponse?.defaultBrandId;
    }

    const selectedBrandExists = this.defaultBrandOptions.some(brand => {
        return brand.id === this.defaultBrandId;
    });

    this.defaultBrandForm.patchValue({
      defaultBrandId:  selectedBrandExists ? this.getDefaultBrandDropdownValue() : []
    });
  }

  onDefaultBrandDropdownChange(brandIds: number[]): void {
    const selectedBrandId = brandIds[0];

    this.brandToSetDefault = this.defaultBrandOptions.find(brand => brand.id === selectedBrandId) ?? null;
  }

  onUpdateDefaultBrandButtonClick(){
    this.showDefaultBrandUpdateModal = true;
  }

  async cancelUpdateDefaultBrand(){
    await this.resetDefaultBrandSettings(false);
  }

  async updateDefaultBrand(){
    try {
      this.updatingDefaultBrand = true;

      await firstValueFrom(this.brandsService.apiBrandsIdSetAsDefaultBrandPatch(this.brandToSetDefault!.id));
      this.defaultBrandId = this.brandToSetDefault!.id;

      await this.resetDefaultBrandSettings(true);
    }
    catch (error) {
      console.error('Error updating default brand:', error);
    }
  }

  private async resetDefaultBrandSettings(reloadCompanyData: boolean)
  {
      this.brandToSetDefault = null;
      this.updatingDefaultBrand = false;
      await this.setDefaultBrand(reloadCompanyData);
      this.showDefaultBrandUpdateModal = false;
  }

  get showBrandRequiredNotice(): boolean {
    return !this.loading && this.totalRecords === 0;
  }
}
